/**
 * Google Ads Text Asset Character Limits
 *
 * These are the official character limits for various Google Ads text assets.
 * Reference: https://support.google.com/google-ads/answer/7684791
 */

// =============================================================================
// Character Limits (shared across ad types)
// =============================================================================

/** Maximum characters for standard headlines (RSA, RDA) */
export const HEADLINE_CHAR_LIMIT = 30;

/** Maximum characters for long headlines (RDA only) */
export const LONG_HEADLINE_CHAR_LIMIT = 90;

/** Maximum characters for descriptions (RSA, RDA) */
export const DESCRIPTION_CHAR_LIMIT = 90;

/** Maximum characters for business name (RDA only) */
export const BUSINESS_NAME_CHAR_LIMIT = 25;

// =============================================================================
// Responsive Search Ads (RSA) Limits
// =============================================================================

export const RSA_LIMITS = {
  /** Minimum headlines required for RSA */
  MIN_HEADLINES: 3,
  /** Maximum headlines allowed for RSA */
  MAX_HEADLINES: 15,
  /** Minimum descriptions required for RSA */
  MIN_DESCRIPTIONS: 2,
  /** Maximum descriptions allowed for RSA */
  MAX_DESCRIPTIONS: 4,
  /** Character limit per headline */
  HEADLINE_CHARS: HEADLINE_CHAR_LIMIT,
  /** Character limit per description */
  DESCRIPTION_CHARS: DESCRIPTION_CHAR_LIMIT,
} as const;

// =============================================================================
// Responsive Display Ads (RDA) Limits
// =============================================================================

export const RDA_LIMITS = {
  /** Minimum headlines required for RDA */
  MIN_HEADLINES: 1,
  /** Maximum headlines allowed for RDA */
  MAX_HEADLINES: 5,
  /** Minimum descriptions required for RDA */
  MIN_DESCRIPTIONS: 1,
  /** Maximum descriptions allowed for RDA */
  MAX_DESCRIPTIONS: 5,
  /** Character limit per headline */
  HEADLINE_CHARS: HEADLINE_CHAR_LIMIT,
  /** Character limit for long headline */
  LONG_HEADLINE_CHARS: LONG_HEADLINE_CHAR_LIMIT,
  /** Character limit per description */
  DESCRIPTION_CHARS: DESCRIPTION_CHAR_LIMIT,
  /** Character limit for business name */
  BUSINESS_NAME_CHARS: BUSINESS_NAME_CHAR_LIMIT,
} as const;

// =============================================================================
// Demand Gen Ads Limits
// =============================================================================

/** Maximum characters for Demand Gen headlines (40 chars, unlike RSA's 30) */
export const DEMAND_GEN_HEADLINE_CHAR_LIMIT = 40;

export const DEMAND_GEN_LIMITS = {
  /** Minimum headlines required for Multi-Asset and Video ads */
  MIN_HEADLINES: 1,
  /** Maximum headlines allowed for Multi-Asset and Video ads */
  MAX_HEADLINES: 5,
  /** Minimum descriptions required for Multi-Asset and Video ads */
  MIN_DESCRIPTIONS: 1,
  /** Maximum descriptions allowed for Multi-Asset and Video ads */
  MAX_DESCRIPTIONS: 5,
  /** Minimum long headlines required for Video ads */
  MIN_LONG_HEADLINES: 1,
  /** Maximum long headlines allowed for Video ads */
  MAX_LONG_HEADLINES: 5,
  /** Character limit per headline (40 chars for Demand Gen) */
  HEADLINE_CHARS: DEMAND_GEN_HEADLINE_CHAR_LIMIT,
  /** Character limit for long headlines */
  LONG_HEADLINE_CHARS: LONG_HEADLINE_CHAR_LIMIT,
  /** Character limit per description */
  DESCRIPTION_CHARS: DESCRIPTION_CHAR_LIMIT,
  /** Character limit for business name */
  BUSINESS_NAME_CHARS: BUSINESS_NAME_CHAR_LIMIT,
  /** Minimum carousel cards required */
  MIN_CAROUSEL_CARDS: 2,
  /** Maximum carousel cards allowed */
  MAX_CAROUSEL_CARDS: 10,
  /** Carousel card headline character limit */
  CAROUSEL_HEADLINE_CHARS: 40,
} as const;

// =============================================================================
// Text Asset Limits (for asset-level management)
// =============================================================================

export const TEXT_ASSET_LIMITS = {
  /** Headline character limit */
  HEADLINE: HEADLINE_CHAR_LIMIT,
  /** Description character limit */
  DESCRIPTION: DESCRIPTION_CHAR_LIMIT,
  /** Long headline character limit */
  LONG_HEADLINE: LONG_HEADLINE_CHAR_LIMIT,
  /** Business name character limit */
  BUSINESS_NAME: BUSINESS_NAME_CHAR_LIMIT,
  /** Sitelink text character limit */
  SITELINK: 25,
  /** Callout text character limit */
  CALLOUT: 25,
  /** Structured snippet header character limit */
  STRUCTURED_SNIPPET_HEADER: 25,
  /** Structured snippet value character limit */
  STRUCTURED_SNIPPET_VALUE: 25,
} as const;
